// Frontend-side mirror of the provider identifiers used by
// supabase/functions/_shared/storage-provider.ts and the storage_connections
// table's `provider` column. Centralizes the naming conventions (edge
// function name, OAuth popup postMessage source, callback route segment,
// display name) so connect/disconnect hooks and the OAuth callback page
// don't each hardcode Drive-specific strings.
export type StorageProviderId = 'google_drive' | 'dropbox' | 'box' | 'sharepoint';

export interface StorageProviderConfig {
  id: StorageProviderId;
  displayName: string;
  /** Edge function implementing this provider's OAuth lifecycle (get_auth_url/exchange_code/refresh_token actions). */
  authFunction: string;
  /** postMessage `source` field used by the OAuth popup to talk back to its opener. */
  postMessageSource: string;
  /** URL segment in /oauth/:segment/callback. Kept separate from `id` because Google's segment ("google-drive") predates and doesn't match the underscored provider id used in the database. */
  callbackSegment: string;
}

export const STORAGE_PROVIDERS: Record<StorageProviderId, StorageProviderConfig> = {
  google_drive: {
    id: 'google_drive',
    displayName: 'Google Drive',
    authFunction: 'google-drive-auth',
    postMessageSource: 'gdrive-oauth',
    callbackSegment: 'google-drive',
  },
  dropbox: {
    id: 'dropbox',
    displayName: 'Dropbox',
    authFunction: 'dropbox-auth',
    postMessageSource: 'dropbox-oauth',
    callbackSegment: 'dropbox',
  },
  box: {
    id: 'box',
    displayName: 'Box',
    authFunction: 'box-auth',
    postMessageSource: 'box-oauth',
    callbackSegment: 'box',
  },
  sharepoint: {
    id: 'sharepoint',
    // Provider id stays 'sharepoint' for consistency with the type/DB/config
    // surface this was wired into ahead of time, but this phase only ever
    // connects a signed-in user's own OneDrive (/me/drive) -- personal or
    // work/school -- never a SharePoint team site's document library
    // (/sites/{id}/drive, a distinct, unimplemented org-level concept). Every
    // user-facing string reads this field, so "SharePoint" here would imply
    // access this integration doesn't have.
    displayName: 'Microsoft OneDrive',
    authFunction: 'sharepoint-auth',
    postMessageSource: 'sharepoint-oauth',
    callbackSegment: 'sharepoint',
  },
};

export const getStorageProviderConfig = (provider: StorageProviderId): StorageProviderConfig =>
  STORAGE_PROVIDERS[provider];

export const providerSegmentToId = (segment: string): StorageProviderId | null => {
  const entry = Object.values(STORAGE_PROVIDERS).find((p) => p.callbackSegment === segment);
  return entry?.id ?? null;
};
